import { SubmissionState } from "@/api/submission-types";
import { ValidationAction, ValidationAssetType } from "@/api/validation-types";
import { RejectWholeEpisodeDialog } from "@/components/episodes/RejectWholeEpisodeDialog";
import { ImageDetails } from "@/components/images/ImageDetails";
import { ApiErrorSnackbar } from "@/components/ui/Snackbar";
import { RejectionDialog } from "@/components/validation/RejectionDialog";
import { ValidationControls } from "@/components/validation/ValidationControls";
import { useIsChildPresent } from "@/hooks/use-is-child-present";
import { useRejectWholeEpisode } from "@/hooks/use-reject-whole-episode";
import { useImageById } from "@/queries/images/use-image-by-id";
import { useValidateImage } from "@/queries/validation/use-validate-image";
import { Box } from "@mui/material";
import { useState } from "react";
import { useParams } from "react-router";

export function ImagePage() {
  const { id } = useParams();

  const [mode, setMode] = useState<"browse" | "validate">("browse");
  const [open, setOpen] = useState(false);

  const { mutate: validateImage, error: validateImageError } =
    useValidateImage();
  const { data: imageData } = useImageById(id);

  const isCheckboxDisabled =
    mode === "browse" ||
    imageData?.submissionState !== SubmissionState.unsubmitted;

  const {
    isChildPresent,
    toggleIsChildPresent: toggleCharacter,
    error: changeIsChildPresentedError,
  } = useIsChildPresent(id!, isCheckboxDisabled);

  const {
    confirmRejectWholeEpisode,
    setConfirmRejectWholeEpisode,
    handleRejectWholeEpisode,
    isRejectingWholeEpisode,
    canRejectEpisode,
  } = useRejectWholeEpisode({
    episodeId: imageData?.episode.id || "",
    shouldNavigateToNext: mode === "validate",
  });

  if (!id) {
    return null;
  }

  const accept = () => {
    validateImage(
      { id, action: ValidationAction.accept },
      {
        onSuccess: () => {
          setMode("browse");
        },
      },
    );
  };

  const reject = (reasons: string[], notes?: string) => {
    validateImage(
      {
        id,
        action: ValidationAction.reject,
        rejectionReasons: reasons,
        notes,
      },
      {
        onSuccess: () => {
          setMode("browse");
        },
        onSettled: () => {
          setOpen(false);
        },
      },
    );
  };

  return (
    <Box sx={{ marginBottom: mode === "browse" ? 2 : 13 }}>
      <ApiErrorSnackbar
        error={validateImageError || changeIsChildPresentedError}
      />
      <ImageDetails
        backButton
        mode={mode}
        id={id}
        onModeChange={setMode}
        isChildPresent={isChildPresent}
        onToggleCharacter={toggleCharacter}
        onRejectWholeEpisode={
          canRejectEpisode
            ? () => setConfirmRejectWholeEpisode(true)
            : undefined
        }
      />
      {mode === "validate" && (
        <>
          <RejectionDialog
            asset={{ assetId: id, type: ValidationAssetType.image }}
            open={open}
            onClose={() => setOpen(false)}
            onReject={reject}
          />
          <ValidationControls
            disabled={open || confirmRejectWholeEpisode}
            onReject={() => setOpen(true)}
            onAccept={accept}
            showCharacterToggle={true}
            onToggleCharacter={toggleCharacter}
          />
        </>
      )}
      <RejectWholeEpisodeDialog
        open={confirmRejectWholeEpisode}
        onClose={() => setConfirmRejectWholeEpisode(false)}
        onConfirm={handleRejectWholeEpisode}
        isLoading={isRejectingWholeEpisode}
      />
    </Box>
  );
}
